import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Sparkles, X } from 'lucide-react';
import { queryKeys } from '@/lib/api/queryKeys';
import { eventsApi } from '@/features/events/api';
import { useT } from '@/lib/i18n/useT';

/**
 * «Ya tienes estaciones — ahora diseña tu Xurvey.»
 *
 * El hueco que tapa: tras montar el evento y sus estaciones en PLAN, el
 * siguiente paso vive en otra pestaña (Design Journey) y nada lo decía. Los
 * managers daban el evento por terminado con la encuesta en su plantilla por
 * defecto, sin marca, y lo descubrían el día del evento.
 *
 * Aparece sólo cuando hay estaciones (antes no hay nada que diseñar) y se
 * puede cerrar; el cierre se recuerda POR EVENTO en localStorage, porque en
 * el siguiente evento la duda vuelve a ser real. No usa el despliegue como
 * señal de «ya diseñado»: desde este cambio las estaciones nacen desplegadas
 * con la plantilla por defecto, así que tener enlace ya no significa haber
 * pasado por el diseño.
 */
export function DesignJourneyNudge({
  eventId,
  onGo,
}: {
  eventId: number;
  onGo: () => void;
}) {
  const t = useT();
  const clave = `xk-dj-nudge-${eventId}`;
  const [cerrado, setCerrado] = useState(() => localStorage.getItem(clave) === '1');

  const stationsQ = useQuery({
    queryKey: queryKeys.stations.byEvent(eventId),
    queryFn: () => eventsApi.listStations({ eventId }),
  });
  const estaciones = stationsQ.data?.items?.length ?? 0;

  if (cerrado || estaciones === 0) return null;

  return (
    <div className="mb-4 flex items-center gap-3 rounded-xl border border-[var(--color-accent-30)] bg-[var(--color-accent-06)] px-4 py-3">
      <Sparkles className="h-5 w-5 flex-none text-[var(--color-accent-ink)]" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-[var(--color-fg-primary)]">
          {t('events.djNudge.title')}
        </p>
        <p className="text-xs text-[var(--color-fg-muted)]">
          {t('events.djNudge.body', { n: String(estaciones) })}
        </p>
      </div>
      <button
        type="button"
        onClick={onGo}
        className="inline-flex flex-none items-center gap-1.5 rounded-lg bg-[var(--color-cta-primary)] px-3 py-1.5 text-sm font-semibold text-[var(--color-fg-on-cta)] hover:bg-[var(--color-cta-primary-hover)]"
      >
        {t('events.djNudge.cta')}
        <ArrowRight className="h-4 w-4" />
      </button>
      <button
        type="button"
        aria-label={t('common.close')}
        onClick={() => {
          localStorage.setItem(clave, '1');
          setCerrado(true);
        }}
        className="flex-none rounded p-1 text-[var(--color-fg-muted)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-fg-primary)]"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
